<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogScrollContent size="lg"  @interactOutside.prevent>
      <form @submit.prevent="onSubmit">
        <DialogHeader>
          <DialogTitle>
            {{ $t('Field configuration') }}
          </DialogTitle>
        </DialogHeader>

        <div>
          <div class="form-group">
            <label for="frm_extrafield_name">{{ $t('Reference') }} {{ $t('(internal)') }}</label>
            <input type="text"
                   id="frm_extrafield_name"
                   :value="fieldEdited.name"
                   readonly
                   disabled
                   class="form-control" />
            <small class="tw:mt-1 tw:block tw:text-xs tw:text-muted-foreground">{{ $t('Auto-generated from the default locale translation') }}</small>
          </div>
          <div class="form-group">
            <label for="frm_extrafield_type">
              {{ $t('Field type') }}
            </label>
            <select
              id="frm_extrafield_type"
              :value="fieldEdited.type"
              class="form-control"
              @change="onUpdateField('type', $event.target.value)"
            >
              <option v-for="type in typesAvailable"
                      :key="type.value"
                      :value="type.value">
                {{ type.label }}
              </option>
            </select>
          </div>

          <TranslatableForm
            :data-locales="locales"
            :data-default-locale="defaultLocale">
            <template #default="{ lang }">
              <div class="form-group">
                <label for="frm_extrafield_translation_value">{{ $t('Field label') }} {{ $t('(public)') }}<span class="text-danger">*</span></label>
                <div v-for="locale in locales" :key="locale.value">
                  <input
                    id="frm_extrafield_translation_value"
                    type="text"
                    :value="fieldEdited.translations.find((translation) => translation.locale === locale.value)?.value"
                    @input="updateTranslation(locale.value, $event.target.value)"
                    class="form-control"
                    :data-locale="locale.value"
                    v-show="lang === locale.value"
                  >
                </div>
              </div>
              <div v-if="fieldDefinition.withText">
                <div class="form-group">
                  <label :for="'field_text_' + lang">{{ $t('Text') }}  {{ $t('(public)') }}</label>
                  <div v-for="locale in locales" :key="locale.value">
                    <TipTapEditor :name="'field_text_' + lang"
                                  :id="'field_text_' + lang"
                                  :value="fieldEdited.text.find((translation) => translation.locale === locale.value)?.value"
                                  @input="onUpdateText(locale.value, $event)"
                                  :data-locale="locale.value"
                                  v-show="lang === locale.value">
                    </TipTapEditor>
                  </div>
                </div>
              </div>
            </template>
          </TranslatableForm>

          <div v-if="fieldDefinition.withOptions">
            <hr>
            <div class="tw:flex tw:items-center tw:justify-between tw:pb-3">
              <h6 class="tw:mb-0">{{ $t('Options') }}</h6>
              <Button type="button" variant="outline" size="sm" @click.prevent="onClickAddChoice">
                {{ $t('Add new option') }}
              </Button>
            </div>
            <Sortable
              :list="fieldOptions"
              item-key="uuid"
              tag="ul"
              :options="{handle:'.handle'}"
              class="tw:flex tw:flex-col tw:divide-y tw:rounded-md tw:border"
              @sort="onDraggableEnd"
            >
              <template #item="{element: choice, index}">
                <li :key="choice.uuid">
                  <div class="tw:flex tw:justify-between tw:items-center tw:px-3 tw:py-2 tw:gap-2">
                    <GripVerticalIcon class="handle tw:size-4 tw:cursor-grab tw:text-muted-foreground" />
                    <span class="tw:flex-1 tw:truncate">{{ choice.name?.trim() || '—' }}</span>
                    <LocaleStatusBadge
                      v-for="locale in locales"
                      :key="locale.value"
                      size="sm"
                      :locale="locale"
                      :completed="!!choice.translations?.find(t => t.locale === locale.value)?.value"
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger as-child>
                        <Button variant="outline" size="sm">
                          {{ $t('Actions') }}
                          <ChevronDownIcon class="tw:ml-2 tw:h-4 tw:w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem @click.prevent="onClickEditChoice(choice, index)">
                          <PenIcon class="tw:size-4" />
                          {{ $t('Edit') }}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" @click.prevent="onRequestDeleteChoice(index)">
                          <Trash2Icon class="tw:size-4" />
                          {{ $t('Delete') }}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              </template>
            </Sortable>

            <!-- Choice editing modal -->
            <Dialog :open="showChoiceModal" @update:open="showChoiceModal = $event">
              <DialogScrollContent>
                <form @submit.prevent="onSubmitSaveChoice">
                  <DialogHeader>
                    <DialogTitle>
                      {{ choiceToEditIndex === -1 ? $t('Add new option') : $t('Edit option') }}
                    </DialogTitle>
                  </DialogHeader>
                  <div class="py-4">
                    <div class="form-group">
                      <label>{{ $t('Reference') }} {{ $t('(internal)') }}</label>
                      <input type="text"
                             :value="choiceToEdit.name"
                             readonly
                             disabled
                             class="form-control" />
                      <small class="tw:mt-1 tw:block tw:text-xs tw:text-muted-foreground">{{ $t('Auto-generated from the default locale translation') }}</small>
                    </div>
                    <TranslatableForm
                      :data-locales="locales"
                      :data-default-locale="defaultLocale">
                      <template #default="{ lang }">
                        <div class="form-group">
                          <label>{{ $t('Option label') }} {{ $t('(public)') }}<span class="text-danger">*</span></label>
                          <div v-for="locale in locales" :key="locale.value">
                            <input
                              type="text"
                              :value="choiceToEdit.translations?.find(t => t.locale === locale.value)?.value"
                              @input="updateChoiceTranslation(locale.value, $event.target.value)"
                              class="form-control"
                              :data-locale="locale.value"
                              v-show="lang === locale.value"
                            >
                          </div>
                        </div>
                      </template>
                    </TranslatableForm>
                  </div>
                  <DialogFooter>
                    <Button type="submit"
                            :disabled="!hasValidChoiceTranslation">
                      {{ $t('Save') }}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogScrollContent>
            </Dialog>

            <!-- Delete confirmation dialog -->
            <AlertDialog :open="showDeleteChoiceDialog" @openChange="showDeleteChoiceDialog = $event">
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{{ $t('Confirm deletion') }}</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogDescription>
                  {{ $t('Are you sure you want to delete this option?') }}<br/>
                  {{ $t('This action cannot be undone.') }}
                </AlertDialogDescription>
                <AlertDialogFooter>
                  <AlertDialogCancel as-child>
                    <Button variant="secondary" @click="showDeleteChoiceDialog = false">
                      {{ $t('Cancel') }}
                    </Button>
                  </AlertDialogCancel>
                  <AlertDialogAction variant="destructive" @click.prevent="onDeleteChoice">
                    {{ $t('Delete') }}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <DialogFooter  class="tw:mt-4">
          <Button
            type="submit"
            :disabled="!hasValidDefaultTranslation || !fieldEdited.type"
          >
            {{ $t('Save') }}
          </Button>
        </DialogFooter>
      </form>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Sortable } from "sortablejs-vue3";
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn/components/ui/alert-dialog";
import TranslatableForm from "@/components/TranslatableForm.vue";
import LocaleStatusBadge from "@/components/ui/LocaleStatusBadge.vue";
import TipTapEditor from "@/components/wysiwyg/TipTapEditor.vue";
import { toSnakeCase, makeUniqueName, generateUUID } from "@/utils/string";
import { Button } from "@/shadcn/components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/shadcn/components/ui/dropdown-menu/index.js";
import { ChevronDownIcon, GripVerticalIcon, PenIcon, Trash2Icon } from "@lucide/vue";

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  field: {
    type: Object,
    default: () => ({})
  },
  locales: {
    type: Array,
    required: true
  },
  defaultLocale: {
    type: String,
    required: true
  },
  typesAvailable: {
    type: Array,
    required: true
  },
  existingFieldNames: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:open', 'save', 'delete']);

// Choice modal
const showChoiceModal = ref(false);
const choiceToEdit = ref({});
const choiceToEditIndex = ref(-1);

// Delete confirmation states
const showDeleteChoiceDialog = ref(false);
const choiceToDeleteIndex = ref(-1);

// Initialize fieldEdited with default values
const getDefaultField = (field = {}) => ({
  name: '',
  type: '',
  translations: [],
  text: [],
  ...JSON.parse(JSON.stringify(field))
});

const getFieldOptions = (choices = []) =>
  (choices?.map(choice => ({
    uuid: generateUUID(),
    ...choice,
  })) || []);

const fieldEdited = ref(getDefaultField());
const fieldOptions = ref([]);

// Synchronize with the prop field when the modal opens
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    fieldEdited.value = getDefaultField(props.field);
    fieldOptions.value = getFieldOptions(fieldEdited.value.choices);
  }
});

// Computed properties
const fieldDefinition = computed(() => {
  if (!fieldEdited.value.type) {
    return {};
  }
  const typeDefinition = props.typesAvailable.find((ta) => ta.value === fieldEdited.value.type);
  return typeDefinition?.definition ?? {};
});

const formatedFieldOptions = computed(() => {
  return fieldOptions.value.map((option) => ({
    name: option.name,
    translations: option.translations,
  }));
});

// Check if the default locale translation is valid
const hasValidDefaultTranslation = computed(() => {
  const defaultTranslation = fieldEdited.value.translations?.find(
    t => t.locale === props.defaultLocale
  );
  return defaultTranslation?.value?.trim().length > 0;
});

// Methods
const onDraggableEnd = (event) => {
  fieldOptions.value.splice(event.newIndex, 0, fieldOptions.value.splice(event.oldIndex, 1)[0]);
  fieldEdited.value.choices = formatedFieldOptions.value;
};

// Initialize a new choice with empty translations for all locales
const getDefaultChoice = (choice = {}) => {
  const translations = props.locales.map(locale => {
    const existing = choice.translations?.find(t => t.locale === locale.value);
    return {
      locale: locale.value,
      value: existing?.value ?? ''
    };
  });

  return {
    name: choice.name ?? '',
    translations
  };
};

// Open the modal to add a new choice
const onClickAddChoice = () => {
  choiceToEdit.value = getDefaultChoice();
  choiceToEditIndex.value = -1;
  showChoiceModal.value = true;
};

// Open the modal to edit an existing choice
const onClickEditChoice = (choice, index) => {
  choiceToEdit.value = getDefaultChoice(choice);
  choiceToEditIndex.value = index;
  showChoiceModal.value = true;
};

// Update the translation of a choice in the modal
const updateChoiceTranslation = (locale, value) => {
  const translationIndex = choiceToEdit.value.translations.findIndex(t => t.locale === locale);

  if (translationIndex >= 0) {
    choiceToEdit.value.translations[translationIndex].value = value;
  } else {
    choiceToEdit.value.translations.push({ locale, value });
  }

  // Regenerate the name only if :
  // - It's the default locale that has been modified
  // - AND the choice doesn't have a name set yet
  const originalChoiceName = choiceToEditIndex.value >= 0
    ? fieldOptions.value[choiceToEditIndex.value]?.name
    : null;

  if (locale === props.defaultLocale && !originalChoiceName) {
    const baseName = toSnakeCase(value);
    const existingChoiceNames = fieldOptions.value
      .filter((_, index) => index !== choiceToEditIndex.value)
      .map(c => c.name);
    choiceToEdit.value.name = makeUniqueName(baseName, existingChoiceNames, null);
  }
};

// Check if the default locale translation is valid for the choice
const hasValidChoiceTranslation = computed(() => {
  const defaultTranslation = choiceToEdit.value.translations?.find(
    t => t.locale === props.defaultLocale
  );
  return defaultTranslation?.value?.trim().length > 0;
});

// Save the choice (add or update)
const onSubmitSaveChoice = () => {
  const choiceData = {
    name: choiceToEdit.value.name,
    translations: [...choiceToEdit.value.translations]
  };

  if (choiceToEditIndex.value === -1) {
    fieldOptions.value.push({
      uuid: generateUUID(),
      ...choiceData
    });
  } else {
    fieldOptions.value[choiceToEditIndex.value] = {
      ...fieldOptions.value[choiceToEditIndex.value],
      ...choiceData
    };
  }

  fieldEdited.value.choices = formatedFieldOptions.value;
  showChoiceModal.value = false;
};

const onUpdateField = (field, value) => {
  fieldEdited.value[field] = value;
};

const onUpdateText = (locale, value) => {
  const i = fieldEdited.value.text.findIndex((translation) => translation.locale === locale);
  if (i < 0) {
    fieldEdited.value.text.push({ locale, value });
  } else {
    fieldEdited.value.text[i].value = value;
  }
};

// Generate the name from the defaultLocale translation
const generateNameFromDefaultLocale = () => {
  const defaultTranslation = fieldEdited.value.translations.find(
    t => t.locale === props.defaultLocale
  );
  const baseName = toSnakeCase(defaultTranslation?.value ?? '');
  const currentName = props.field.name;
  return makeUniqueName(baseName, props.existingFieldNames, currentName);
};

const updateTranslation = (locale, value) => {
  const i = fieldEdited.value.translations.findIndex((translation) => translation.locale === locale);
  if (i < 0) {
    fieldEdited.value.translations.push({ locale, value });
  } else {
    fieldEdited.value.translations[i].value = value;
  }

  // Regenerate the name only if :
  // - It's the default locale that has been modified
  // - AND the field doesn't have a name set yet
  if (locale === props.defaultLocale && !props.field.name) {
    fieldEdited.value.name = generateNameFromDefaultLocale();
  }
};

const onSubmit = () => {
  emit('save', fieldEdited.value);
  emit('update:open', false);
};

// Request to delete a choice
const onRequestDeleteChoice = (index) => {
  choiceToDeleteIndex.value = index;
  showDeleteChoiceDialog.value = true;
};

// Delete the choice
const onDeleteChoice = () => {
  fieldOptions.value.splice(choiceToDeleteIndex.value, 1);
  fieldEdited.value.choices = formatedFieldOptions.value;
  showDeleteChoiceDialog.value = false;
};
</script>

